import { ListDataParams, ListDataResponse } from '@api-client/types';
import {
  AccountsInterface,
  DivisionsInterface,
  InvoiceInterface
} from '@interfaces/invoice.interface';
import { ListQuoteResponseInterface } from '@interfaces/Quotes.interface';
import BaseApiClient from '../BaseApiClient';

export class ApiXero extends BaseApiClient {
  xero = '/xero-invoices';

  async index(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<InvoiceInterface>> {
    if (searchTerm) {
      if (!listData.filters) listData.filters = {};
      listData.filters.q = searchTerm;
    }
    const res = await this.client.getList(this.xero, listData);

    return {
      items: res.data.data,
      pagination: {
        page: res.data.meta?.current_page,
        perPage: res.data.meta?.per_page,
        total: res.data.meta?.total
      }
    };
  }

  async create(payload: unknown): Promise<InvoiceInterface> {
    return (await this.client.post(this.xero, payload)).data;
  }

  async view(invoiceId: number) {
    return (await this.client.get(`${this.xero}/${invoiceId}`)).data;
  }

  async update(invoiceId: number, payload: unknown) {
    return await this.client.put(`${this.xero}/${invoiceId}`, payload);
  }

  async cancel(id: number) {
    return await this.client.put(this.xero, { data: [{ id }] });
  }

  async send(invoiceId: number, payload: unknown) {
    return await this.client.post(`${this.xero}/${invoiceId}/send`, payload);
  }

  async markAsSent(invoiceId: number) {
    return await this.client.post(`${this.xero}/${invoiceId}/mark-as-sent`);
  }

  async paidStatus(
    invoiceId: number,
    paid: 'paid' | 'unpaid'
  ): Promise<InvoiceInterface> {
    return await this.client.post(`${this.xero}/${invoiceId}/paid-status`, {
      paid
    });
  }

  async termsAndConditions(invoiceId: number) {
    return await this.client.put(
      `${this.xero}/${invoiceId}/terms-and-conditions`
    );
  }

  async getAccounts(): Promise<AccountsInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/xero/accounts')
        .then((res) => {
          if (res.data) {
            resolve(res.data.accounts);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getDivisionsRecurrences(): Promise<DivisionsInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/xero/divisions-recurrences')
        .then((res) => {
          if (res.data) {
            resolve(res.data.divisions);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async createItem(invoiceId: number, payload: unknown) {
    return await this.client.post(`${this.xero}/${invoiceId}/items`, payload);
  }

  async viewItem(invoiceId: number, itemId: number) {
    return await this.client.get(`${this.xero}/${invoiceId}/items/${itemId}`);
  }

  async updateItem(invoiceId: number, itemId: number, payload: unknown) {
    return await this.client.put(
      `${this.xero}/${invoiceId}/items/${itemId}`,
      payload
    );
  }

  async deleteItem(invoiceId: number, itemId: string) {
    return await this.client.delete(
      `${this.xero}/${invoiceId}/items?item_ids=${itemId}`
    );
  }

  async convertToInvoice(
    id: number,
    body: Partial<InvoiceInterface>
  ): Promise<ListQuoteResponseInterface> {
    return (
      await this.client.post(
        `/simple-quotes/${id}/convert-to-xero-invoice`,
        body
      )
    ).data;
  }
}

export { xeroQueryKeys } from './query-keys';
export type { XeroInvoicesIndexQueryParams } from './query-keys';
export {
  useXeroAccountsQuery,
  useXeroDivisionsRecurrencesQuery,
  useXeroInvoiceItemQuery,
  useXeroInvoiceViewQuery,
  useXeroInvoicesIndexQuery
} from './use-xero-queries';
export {
  useXeroConvertToInvoiceMutation,
  useXeroInvoiceCancelMutation,
  useXeroInvoiceCreateMutation,
  useXeroInvoiceItemCreateMutation,
  useXeroInvoiceItemDeleteMutation,
  useXeroInvoiceItemUpdateMutation,
  useXeroInvoiceMarkAsSentMutation,
  useXeroInvoicePaidStatusMutation,
  useXeroInvoiceSendMutation,
  useXeroInvoiceTermsAndConditionsMutation,
  useXeroInvoiceUpdateMutation,
  type XeroConvertToInvoiceVariables,
  type XeroInvoiceItemCreateVariables,
  type XeroInvoiceItemDeleteVariables,
  type XeroInvoiceItemUpdateVariables,
  type XeroInvoicePaidStatusVariables,
  type XeroInvoiceSendVariables,
  type XeroInvoiceUpdateVariables
} from './use-xero-mutations';

